import { useState, useCallback, useRef } from 'react';
import { apiRequest } from '../../services/apiMiddleware';
import { API_GATEWAY_PROCESS_NAME, AgentMode } from '../../utils/constants';

export interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface FclmAgentResponse {
  answer: string;
  nodeId: string;
}

interface ShiftPlanAgentResponse extends FclmAgentResponse {
  planId: string;
}

const MAX_HISTORY_TURNS = 6;
const API_TIMEOUT_MS = 30000;

export const useAgentChat = (
  nodeId: string,
  mode: AgentMode,
  planId?: string | null,
  date?: string | null
) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const isLoadingRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);
  const idCounter = useRef(0);
  const messagesRef = useRef<Message[]>([]);

  const generateId = () => {
    idCounter.current += 1;
    return `msg-${idCounter.current}-${Date.now()}`;
  };

  const buildHistory = (currentMessages: Message[]) => {
    const historyMessages = currentMessages.slice(-(MAX_HISTORY_TURNS * 2));
    return historyMessages.map(({ role, content }) => ({ role, content }));
  };

  const sendMessage = useCallback(async (question: string) => {
    if (!question.trim() || isLoadingRef.current) return;

    // Abort any in-flight request
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    const { signal } = abortRef.current;

    isLoadingRef.current = true;
    setIsLoading(true);

    const userMessage: Message = {
      id: generateId(),
      role: 'user',
      content: question.trim(),
      timestamp: new Date(),
    };

    const updatedMessages = [...messagesRef.current, userMessage];
    messagesRef.current = updatedMessages;
    setMessages(updatedMessages);

    const history = buildHistory(updatedMessages);

    // Route to the correct endpoint and build request body based on mode
    let resourcePath: string;
    let requestBody: Record<string, any>;

    switch (mode) {
      case AgentMode.SHIFT_PLAN:
        resourcePath = '/rcc-agent/explain';
        requestBody = {
          question: question.trim(),
          nodeId,
          warehouseId: nodeId,
          ...(date && { date }),
          ...(planId && { planId }),
          history,
        };
        break;
      case AgentMode.SHIFT_TRACKING:
        resourcePath = '/rcc-agent/query';
        requestBody = {
          question: question.trim(),
          nodeId,
          warehouseId: nodeId,
          history,
        };
        break;
    }

    try {
      const response = await apiRequest<FclmAgentResponse | ShiftPlanAgentResponse>({
        resourcePath,
        httpMethod: 'post',
        processName: API_GATEWAY_PROCESS_NAME,
        requestBody,
        timeout: API_TIMEOUT_MS,
      });

      // Discard response if request was aborted (e.g., user reset chat)
      if (signal.aborted) return;

      const assistantMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: response.data?.answer || 'Sorry, I was unable to process your request.',
        timestamp: new Date(),
      };
      const withAssistant = [...messagesRef.current, assistantMessage];
      messagesRef.current = withAssistant;
      setMessages(withAssistant);
    } catch {
      if (signal.aborted) return;

      const errorMessage: Message = {
        id: generateId(),
        role: 'assistant',
        content: 'Something went wrong. Please try again.',
        timestamp: new Date(),
      };
      const withError = [...messagesRef.current, errorMessage];
      messagesRef.current = withError;
      setMessages(withError);
    } finally {
      if (!signal.aborted) {
        isLoadingRef.current = false;
        setIsLoading(false);
      }
    }
  }, [nodeId, mode, planId, date]);

  const clearMessages = useCallback(() => {
    abortRef.current?.abort();
    messagesRef.current = [];
    setMessages([]);
    isLoadingRef.current = false;
    setIsLoading(false);
  }, []);

  return { messages, isLoading, sendMessage, clearMessages };
};
